import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { v4 as uuid } from 'uuid'
import useRouter from 'utils/useRouter'
import moment from 'moment'
import { useExameComparacaoService } from 'hooks/useExameComparacaoService'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useLocal } from 'hooks/useLocal'

export const useExameComparacao = () => {
  const params = useParams()
  const router = useRouter()
  const exameComparacaoService = useExameComparacaoService()
  const exameRealizadoService = useExameRealizadoService()
  const localService = useLocal()

  const [exameComparacao, setExameComparacao] = useState(null)
  const [estados, setEstados] = useState([])
  const [paises, setPaises] = useState([])
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [processandoRegistrosIdentificacao, setProcessandoRegistrosIdentificacao] = useState(false)
  const [registrosIdentificacao, setRegistrosIdentificacao] = useState([
    {
      uuid: uuid(),
      nome: null,
      cpf: null,
      rg: null,
      filiacoes: [{ uuid: uuid(), nome: null }],
      nacionalidade: null,
      estado: null,
      cidade: null,
      opcoesCidades: [],
      sexo: null,
      alcunha: null,
      numeroProtocolo: null,
      tipoRegistro: null,
      dataNascimento: null,
    },
  ])

  // Form State para o resultado do confronto (substituindo useFormState)
  const [formState, setFormState] = useState({
    values: {
      resultado: null,
    },
    touched: {},
    errors: {},
  })

  // Handlers para o Form State
  const handleChangeNewValueByNameProperty = useCallback((event, newValue, property) => {
    setFormState((prevState) => {
      const newErrors = { ...prevState.errors }
      if (property === 'resultado') {
        if (!newValue) {
          newErrors.resultado = ['Resultado é obrigatório']
        } else {
          delete newErrors.resultado
        }
      }

      return {
        ...prevState,
        values: {
          ...prevState.values,
          [property]: newValue,
        },
        touched: {
          ...prevState.touched,
          [property]: true,
        },
        errors: newErrors,
      }
    })
  }, [])

  const hasError = useCallback((field) => {
    return !!(formState.touched[field] && formState.errors[field])
  }, [formState.touched, formState.errors])

  const form = {
    formState,
    setFormState,
    handleChangeNewValueByNameProperty,
    hasError,
  }

  // Busca inicial de Estados
  const fetchEstados = useCallback(async () => {
    try {
      const response = await localService.listaDeEstadosDoBrasil()
      setEstados(response || [])
    } catch (error) {
      console.error('Falha ao buscar estados:', error)
    }
  }, [])

  // Busca inicial de Países
  const fetchPaises = useCallback(async () => {
    try {
      const response = await localService.listaDePaises()
      setPaises(response || [])
    } catch (error) {
      console.error('Falha ao buscar países:', error)
    }
  }, [])

  // Busca cidades por sigla de estado
  const fetchCidades = useCallback(async (siglaEstado) => {
    try {
      return await localService.listarCidadesPorEstadoAsync(siglaEstado)
    } catch (error) {
      console.error(`Falha ao buscar cidades para o estado ${siglaEstado}:`, error)
      return []
    }
  }, [])

  // Busca dados do Exame de Comparação
  const fetchExameComparacao = useCallback(async () => {
    if (params.id) {
      try {
        const response = await exameComparacaoService.listarExameComparacaoParaExaminadorAsync(params.id)
        setExameComparacao(response)
      } catch (error) {
        console.error('Falha ao buscar exame de comparação:', error)
      }
    }
  }, [params.id])

  // Busca registros de identificação do Exame
  const fetchRegistrosIdentificacaoExame = useCallback(async () => {
    if (params.id) {
      try {
        const response = await exameComparacaoService.listarRegistrosIdentificacaoExameComparacaoAsync(
          params.id,
          setProcessandoRegistrosIdentificacao
        )
        if (response?.length) {
          const estadosList = await localService.listaDeEstadosDoBrasil()
          const cidadesList = await localService.listaCidadesDoBrasilAsync()
          const paisesList = await localService.listaDePaises()

          // Mapeia os registros retornados para o formato utilizado no formulário
          const registrosMapeados = await Promise.all(
            response.map(async (reg) => {
              const est = estadosList.find((e) => e.sigla === reg.estado)
              // Carrega opções de cidades específicas para o estado do registro
              let opcoesCidades = []
              if (est) {
                opcoesCidades = await localService.listarCidadesPorEstadoAsync(est.sigla)
              }

              return {
                ...reg,
                estado: est || null,
                cidade: cidadesList.find((c) => c.nome === reg.cidade) || null,
                nacionalidade: paisesList.find((p) => p.nome === reg.nacionalidade) || null,
                opcoesCidades: opcoesCidades || [],
                uuid: uuid(),
                dataNascimento: reg.dataNascimento ? reg.dataNascimento : null,
                filiacoes: reg.filiacoes?.length
                  ? reg.filiacoes.map((filiacao) => ({ nome: filiacao, uuid: uuid() }))
                  : [{ uuid: uuid(), nome: null }],
              }
            })
          )
          setRegistrosIdentificacao(registrosMapeados)
        } else {
          setRegistrosIdentificacao([
            {
              uuid: uuid(),
              nome: null,
              cpf: null,
              rg: null,
              filiacoes: [{ uuid: uuid(), nome: null }],
              nacionalidade: null,
              estado: null,
              opcoesCidades: [],
              cidade: null,
              sexo: null,
              alcunha: null,
              numeroProtocolo: null,
              tipoRegistro: null,
              dataNascimento: null,
            },
          ])
        }
      } catch (error) {
        console.error('Falha ao buscar registros de identificação:', error)
      }
    }
  }, [params.id])

  // Efeitos de carga inicial
  useEffect(() => {
    fetchPaises()
    fetchEstados()
  }, [fetchPaises, fetchEstados])

  useEffect(() => {
    fetchExameComparacao()
    fetchRegistrosIdentificacaoExame()
  }, [fetchExameComparacao, fetchRegistrosIdentificacaoExame])

  // Atualiza o resultado do formulário quando o exame comparativo é carregado
  useEffect(() => {
    if (exameComparacao?.id) {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          resultado: exameComparacao.resultado,
        },
      }))
    }
  }, [exameComparacao])

  // Lógica para alteração de resultado
  const handleChangeResultado = useCallback(
    (event, value) => {
      if (value === 'NAO_IDENTIFICADO_SISTEMA' || value === 'NAO_IDENTIFICADO_SEM_IMPRESSAO_PAPILAR') {
        setRegistrosIdentificacao([])
      }
      handleChangeNewValueByNameProperty(event, value, 'resultado')
    },
    [handleChangeNewValueByNameProperty]
  )

  // Manipulação de registros de identificação
  const handleClickAdicionarRegistro = useCallback(() => {
    const novoRegistro = {
      uuid: uuid(),
      nome: null,
      alcunha: null,
      cpf: null,
      rg: null,
      filiacoes: [{ uuid: uuid(), nome: null }],
      nacionalidade: null,
      estado: null,
      opcoesCidades: [],
      cidade: null,
      sexo: null,
      numeroProtocolo: null,
      tipoRegistro: null,
      dataNascimento: null,
    }
    setRegistrosIdentificacao((prev) => prev.concat([novoRegistro]))
  }, [])

  const handleClickRemoverRegistro = useCallback((registroUuid) => {
    setRegistrosIdentificacao((prev) => prev.filter((registro) => registro.uuid !== registroUuid))
  }, [])

  const handleChangePropRegistroPeloUuid = useCallback((registroUuid, prop, newValue) => {
    setRegistrosIdentificacao((registrosAntigos) =>
      registrosAntigos.map((registro) =>
        registro.uuid === registroUuid ? { ...registro, [prop]: newValue } : registro
      )
    )
  }, [])

  // Modificação de estado que dispara a busca assíncrona de cidades específicas do estado selecionado
  const handleEstadoChange = useCallback(
    async (registroUuid, estadoSelecionado) => {
      handleChangePropRegistroPeloUuid(registroUuid, 'estado', estadoSelecionado)
      handleChangePropRegistroPeloUuid(registroUuid, 'cidade', null)

      if (estadoSelecionado) {
        const cidades = await fetchCidades(estadoSelecionado.sigla)
        handleChangePropRegistroPeloUuid(registroUuid, 'opcoesCidades', cidades)
      } else {
        handleChangePropRegistroPeloUuid(registroUuid, 'opcoesCidades', [])
      }
    },
    [handleChangePropRegistroPeloUuid, fetchCidades]
  )

  // Manipulação de filiações
  const handleChangeFiliacoesPeloUuidRegistroeUUidFiliacao = useCallback((uuidRegistro, uuidFiliacao, novoValor) => {
    setRegistrosIdentificacao((registrosAntigos) =>
      registrosAntigos.map((registro) =>
        registro.uuid === uuidRegistro
          ? {
            ...registro,
            filiacoes: registro.filiacoes?.map((filiacao) =>
              filiacao.uuid === uuidFiliacao ? { ...filiacao, nome: novoValor } : filiacao
            ),
          }
          : registro
      )
    )
  }, [])

  const getValorFiliacaoPeloUuidRegistroeUuidFiliacao = useCallback(
    (uuidRegistro, uuidFiliacao) => {
      let result = null
      registrosIdentificacao.forEach((registro) => {
        if (registro.uuid === uuidRegistro) {
          registro.filiacoes?.forEach((filiacao) => {
            if (filiacao.uuid === uuidFiliacao) {
              result = filiacao?.nome
            }
          })
        }
      })
      return result
    },
    [registrosIdentificacao]
  )

  const handleAdicionarFiliacaoPelloUuidRegistro = useCallback((uuidRegistro) => {
    setRegistrosIdentificacao((registrosAntigos) =>
      registrosAntigos.map((registro) =>
        registro.uuid === uuidRegistro
          ? {
            ...registro,
            filiacoes: [...(registro.filiacoes || []), { uuid: uuid(), nome: null }],
          }
          : registro
      )
    )
  }, [])

  const getValorPropRegistroPeloUUID = useCallback(
    (prop, uuidRegistro) => {
      let result = null
      registrosIdentificacao.forEach((registro) => {
        if (registro.uuid === uuidRegistro) {
          result = registro[prop]
        }
      })
      return result
    },
    [registrosIdentificacao]
  )

  // Máscara de CPF
  const cpfMask = useCallback((value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }, [])

  // Parsing e formatação de data local sem fuso horário
  const parseDataLocal = useCallback((dateString) => {
    if (!dateString) return null
    if (dateString instanceof Date) return dateString
    const parts = String(dateString).split('-')
    if (parts.length === 3) {
      const [ano, mes, dia] = parts
      return new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia))
    }
    const partsSlash = String(dateString).split('/')
    if (partsSlash.length === 3) {
      const [dia, mes, ano] = partsSlash
      return new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia))
    }
    return new Date(dateString)
  }, [])

  const handleDateChange = useCallback(
    (registroUuid, date) => {
      if (!date || isNaN(Date.parse(date))) {
        handleChangePropRegistroPeloUuid(registroUuid, 'dataNascimento', null)
        return
      }
      const d = new Date(date)
      const ano = d.getFullYear()
      const mes = String(d.getMonth() + 1).padStart(2, '0')
      const dia = String(d.getDate()).padStart(2, '0')

      handleChangePropRegistroPeloUuid(registroUuid, 'dataNascimento', `${ano}-${mes}-${dia}`)
    },
    [handleChangePropRegistroPeloUuid]
  )

  // Validação reativa sem useEffect redundante
  const isFormInvalid = useMemo(() => {
    const resultado = formState.values.resultado
    if (!resultado) return true

    if (
      resultado === 'IDENTIFICADO_SISTEMA_POSITIVO' ||
      resultado === 'IDENTIFICADO_MAIS_DE_UM_PADRAO' ||
      resultado === 'BIOM_CONVERGENTE_BIOG_DIVERGENTE' ||
      resultado === 'BIOM_DIVERGENTE_BIOG_CONVERGENTE'
    ) {
      if (!registrosIdentificacao || registrosIdentificacao.length === 0) {
        return true
      }

      return registrosIdentificacao.some((registro) => {
        // Pelo menos nome ou alcunha deve estar preenchido
        const hasNome = !!registro.nome?.trim()
        const hasAlcunha = !!registro.alcunha?.trim()
        if (!hasNome && !hasAlcunha) return true

        if (!registro.numeroProtocolo?.trim()) return true

        if (registro.tipoRegistro === 'Registro Civil') {
          // Pelo menos uma filiação preenchida
          const hasFiliacao = registro.filiacoes?.some((f) => !!f.nome?.trim())
          if (!hasFiliacao) return true

          if (!registro.rg?.trim()) return true
          if (!registro.sexo) return true
          if (!registro.dataNascimento || !moment(registro.dataNascimento, 'YYYY-MM-DD', true).isValid()) return true
          if (!registro.nacionalidade) return true
          if (!registro.estado) return true
          if (!registro.cidade) return true
        }

        return false
      })
    }

    return false
  }, [formState.values.resultado, registrosIdentificacao])

  // Ações de navegação e salvamento
  const handleVoltar = useCallback(() => {
    router.history.goBack()
  }, [router.history])

  const handleConfirmar = useCallback(async () => {
    setProcessandoOperacao(true)
    try {
      const idPerito = exameComparacao?.exReal?.exSol?.perics?.[0]?.id

      // Tratamento dos registros para envio ao backend, mapeando as classes para strings simples/formatadas
      const registrosTratados = registrosIdentificacao.map((reg) => ({
        ...reg,
        estado: reg.estado?.sigla || null,
        cidade: reg.cidade?.nome || null,
        nacionalidade: reg.nacionalidade?.nome || null,
        filiacoes: reg.filiacoes?.map((f) => f.nome).filter(Boolean) || [],
      }))

      await exameComparacaoService.confirmarDadosExameComparacao(
        params.id,
        idPerito,
        {
          resultado: formState.values.resultado,
          registrosIdentificacao: registrosTratados,
        },
        setProcessandoOperacao
      )

      await exameRealizadoService.gerarDocumento(params.id, setProcessandoOperacao)

      router.history.push({
        pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.id}`,
        state: { IgnoneUploadImagem: true },
      })
    } catch (error) {
      console.error('Falha ao confirmar dados do exame de confronto:', error)
    } finally {
      setProcessandoOperacao(false)
    }
  }, [formState.values.resultado, registrosIdentificacao, params.id, exameComparacao, router.history])

  return {
    exameComparacao,
    estados,
    paises,
    processandoOperacao,
    processandoRegistrosIdentificacao,
    registrosIdentificacao,
    form,
    isFormInvalid,
    handleChangeResultado,
    handleClickAdicionarRegistro,
    handleClickRemoverRegistro,
    handleChangePropRegistroPeloUuid,
    handleEstadoChange,
    handleChangeFiliacoesPeloUuidRegistroeUUidFiliacao,
    getValorFiliacaoPeloUuidRegistroeUuidFiliacao,
    handleAdicionarFiliacaoPelloUuidRegistro,
    getValorPropRegistroPeloUUID,
    cpfMask,
    parseDataLocal,
    handleDateChange,
    handleVoltar,
    handleConfirmar,
  }
}
